<script setup lang="ts">
import { computed, useSlots, type ComputedRef } from 'vue'

const slots = useSlots()

const props = defineProps({
  to: {
    type: String,
    default: null,
  },

  // inline automatically sets underline
  type: {
    type: String,
    default: 'inline',
    validator: (value: string) => ['block', 'inline'].includes(value),
  },

  underline: {
    type: Boolean,
    default: false,
  },

  openInNewTab: {
    type: Boolean,
    default: false,
  },

  openInAppBrowser: {
    type: Boolean,
    default: false,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  // Overrides leadingIcon
  leadingText: {
    type: String,
    default: null,
  },

  leadingIcon: {
    type: String,
    default: null,
  },

  trailingText: {
    type: String,
    default: null,
  },

  trailingIcon: {
    type: String,
    default: null,
  },

  inheritColor: {
    type: Boolean,
    default: false,
  },

  exactRouteMatching: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const willOpenInNewTab = computed(
  () => props.openInNewTab || (typeof props.to === 'string' && props.to.includes('https://')),
)

const isDeepLink = computed(
  () => typeof props.to === 'string'
    && Boolean(props.to)
    && (props.to.includes('mailto:') || props.to.includes('tel:')),
)

const linkClass = computed(() => {
  return [
    {
      'flex items-center': props.type !== 'inline',
      'inline-flex items-center': props.type === 'inline',
      'text-(--ui-primary)-500 hover:text-(--ui-primary)-900': !props.inheritColor,
      'text-current': props.inheritColor,
    },
    'cursor-pointer w-fit',
  ]
})

const hasLeading: ComputedRef<boolean> = computed(() => !!props.leadingText || !!props.leadingIcon || !!slots.leading || false)
const hasTrailing: ComputedRef<boolean> = computed(() => !!props.trailingText || !!props.trailingIcon || !!slots.trailing || false)
</script>

<template>
  <NuxtLink
    :to="props.to"
    :class="linkClass"
    :external="willOpenInNewTab || isDeepLink"
    :target="willOpenInNewTab ? '_blank' : null"
    :exact="props.exactRouteMatching"
    data-testid="anchor"
    class="inline-flex items-center gap-x-1.5"
    @click.stop="emit('click')"
  >
    <!-- @slot Custom loading content, otherwise a spinner. Loading replaces prefix -->
    <slot
      v-if="props.isLoading"
      name="loading"
    >
      <Loader iconOnly />
    </slot>

    <span
      v-if="hasLeading"
      class="flex flex-col justify-center"
    >
      <slot name="leading">
        <template v-if="props.leadingText">{{ props.leadingText }}</template>

        <Icon
          v-else-if="props.leadingIcon"
          data-testid="icon"
          :name="props.leadingIcon"
          class="text-[1.4em]"
        />
      </slot>
    </span>

    <template v-if="props.underline">
      <span class="inline-flex underline">
        <slot class="underline" />
      </span>
    </template>

    <span v-else>
      <slot />
    </span>

    <span
      v-if="hasTrailing"
      class="flex flex-col items-center justify-center"
    >
      <slot name="trailing">
        <template v-if="props.trailingText">{{ props.trailingText }}</template>

        <Icon
          v-else-if="props.trailingIcon"
          :name="props.trailingIcon"
        />
      </slot>
    </span>
  </NuxtLink>
</template>

<style scoped>
a {
  transition: color 0.2s;
}
</style>
